import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChartBar, ArrowsLeftRight, Database, Cube, Lightning, CircleNotch, TrendUp, TrendDown } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, formatBigCount } from "@/lib/format";

// Live transaction-volume panel.
//  • Short ranges (30M/1H/3H) are built from recent blocks — each block carries
//    its real ADA output + tx_count + time, bucketed into bars.
//  • Long ranges (7D/30D) are built from per-epoch totals (one bar per ~5-day
//    epoch) since the API can't serve days of block history within its rate
//    limit. No synthetic data anywhere.
// Every range also fetches the *preceding* window so the Total Volume tile can
// show an honest up/down % vs the previous equal-length period.
const RANGES = [
  { key: "30M", mode: "blocks", minutes: 30, pages: 2, bucketMin: 1 },
  { key: "1H", mode: "blocks", minutes: 60, pages: 4, bucketMin: 1 },
  { key: "3H", mode: "blocks", minutes: 180, pages: 11, bucketMin: 5 },
  { key: "7D", mode: "epochs", epochs: 2 },
  { key: "30D", mode: "epochs", epochs: 6 },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Fetch N pages of recent blocks in throttled batches of 3 to stay under the
// API's concurrency rate limit.
async function fetchBlockPages(pages) {
  const out = [];
  for (let i = 0; i < pages; i += 3) {
    const batch = [];
    for (let p = i + 1; p <= Math.min(pages, i + 3); p++) {
      batch.push(http.get(`${API}/blocks/recent`, { params: { count: 100, page: p }, timeout: 15000 }));
    }
    const res = await Promise.all(batch);
    res.forEach((r) => Array.isArray(r.data) && out.push(...r.data));
    if (i + 3 < pages) await sleep(220);
  }
  return out;
}

// Fetch the latest `count` epochs (most-recent first) in throttled batches.
async function fetchEpochs(count) {
  const latest = (await http.get(`${API}/epochs/latest`, { timeout: 15000 })).data;
  const n = latest.epoch;
  const nums = [];
  for (let i = 0; i < count && n - i >= 0; i++) nums.push(n - i);
  const byNum = { [n]: latest };
  const rest = nums.filter((x) => x !== n);
  for (let i = 0; i < rest.length; i += 3) {
    const batch = rest.slice(i, i + 3).map((x) =>
      http.get(`${API}/epochs/${x}`, { timeout: 15000 }).then((r) => ({ x, d: r.data })).catch(() => null),
    );
    const res = await Promise.all(batch);
    res.forEach((v) => v && (byNum[v.x] = v.d));
    if (i + 3 < rest.length) await sleep(200);
  }
  return nums.map((x) => byNum[x]).filter(Boolean);
}

const hhmm = (unixSec) => {
  const d = new Date(unixSec * 1000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};

const dayLabel = (unixSec) => {
  const d = new Date(unixSec * 1000);
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${d.getUTCDate()} ${mon}`;
};

const peakLabel = (unixSec) => {
  const d = new Date(unixSec * 1000);
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).toUpperCase();
  return `${d.getUTCDate()} ${mon} ${d.getUTCFullYear()}, ${hhmm(unixSec)} UTC`;
};

const TrendBadge = ({ pct }) => {
  if (pct == null || !isFinite(pct)) return null;
  const up = pct >= 0;
  const Icon = up ? TrendUp : TrendDown;
  return (
    <span
      data-testid="tv-volume-trend"
      className={cn(
        "inline-flex items-center gap-1 text-[11px] mono px-1.5 py-0.5 rounded-md shrink-0",
        up ? "text-emerald-400 bg-emerald-400/10" : "text-rose-400 bg-rose-400/10",
      )}
      title="Change vs the previous equal-length window"
    >
      <Icon size={11} weight="bold" />
      {up ? "+" : "−"}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

// Tiny inline sparkline so the volume direction reads at a glance without
// opening the chart. Scales to fill its tile; colour follows the trend.
const Sparkline = ({ data, up }) => {
  const vals = (data || []).map((d) => Number(d.ada) || 0);
  if (vals.length < 2) return null;
  const W = 120, H = 24, pad = 2;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const stepX = (W - pad * 2) / (vals.length - 1);
  const pts = vals.map((v, i) => [
    pad + i * stepX,
    pad + (H - pad * 2) * (1 - (v - min) / span),
  ]);
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${pad},${H - pad} ${line} ${W - pad},${H - pad}`;
  const color = up ? "#34d399" : "#fb7185";
  const gid = up ? "tv-spark-up" : "tv-spark-down";
  const last = pts[pts.length - 1];
  return (
    <svg
      data-testid="tv-volume-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-1 w-full max-w-[150px] h-[24px]"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gid})`} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r={1.8} fill={color} />
    </svg>
  );
};

const StatTile = ({ icon: Icon, label, value, sub, badge, spark, testid }) => (
  <div data-testid={testid} className="flex items-center gap-3 px-4 py-3 min-w-0">
    <div className="w-9 h-9 shrink-0 rounded-lg border border-primary/25 bg-primary/[0.06] flex items-center justify-center" style={{ filter: "drop-shadow(0 0 5px rgba(157,92,255,0.35))" }}>
      <Icon size={18} className="text-primary" weight="duotone" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono whitespace-nowrap">{label}</div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-heading text-[18px] md:text-[20px] leading-tight tabnum text-[#22d3ee] text-glow-cyan whitespace-nowrap">{value}</div>
        {badge}
      </div>
      {sub ? <div className="text-[10px] text-muted-foreground/70 mono mt-0.5 whitespace-nowrap">{sub}</div> : null}
      {spark}
    </div>
  </div>
);

const yAxisFmt = (v) => {
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${n}`;
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur px-3 py-2 text-[11px] mono">
      <div className="text-muted-foreground">{p.tip}</div>
      <div className="text-[#22d3ee] text-glow-cyan">{formatBigCount(p.ada)} ₳</div>
      <div className="text-muted-foreground/70">{formatBigCount(p.txs)} txs</div>
    </div>
  );
};

export const TransactionVolume = () => {
  const [range, setRange] = useState("1H");
  const [blocks, setBlocks] = useState([]);
  const [epochs, setEpochs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);

  const cfg = RANGES.find((r) => r.key === range) || RANGES[1];

  const load = useCallback(async () => {
    const my = ++reqRef.current;
    setLoading(true);
    try {
      if (cfg.mode === "epochs") {
        const eps = await fetchEpochs(cfg.epochs * 2);
        if (my !== reqRef.current) return;
        setEpochs(eps);
      } else {
        const data = await fetchBlockPages(cfg.pages);
        if (my !== reqRef.current) return;
        setBlocks(data);
      }
      setError(null);
    } catch (e) {
      if (my !== reqRef.current) return;
      setError(e.response?.data?.detail || { message: e.message });
    } finally {
      if (my === reqRef.current) setLoading(false);
    }
  }, [cfg.mode, cfg.pages, cfg.epochs]);

  useEffect(() => {
    load();
    const id = setInterval(load, cfg.mode === "epochs" ? 300000 : 30000);
    return () => clearInterval(id);
  }, [load, cfg.mode]);

  const { bars, stats } = useMemo(() => {
    if (cfg.mode === "epochs") {
      const eps = epochs.filter((e) => e && e.epoch != null);
      if (!eps.length) return { bars: [], stats: null };
      const half = cfg.epochs;
      const current = eps.slice(0, half);
      const previous = eps.slice(half, half * 2);
      const sumAda = (arr) => arr.reduce((a, e) => a + Number(e.output || 0) / 1e6, 0);
      const barsSrc = [...current].reverse();
      const bars = barsSrc.map((e) => ({
        label: `E${e.epoch}`,
        tip: `Epoch ${e.epoch} · ${dayLabel(e.start_time)}`,
        ada: Number(e.output || 0) / 1e6,
        txs: e.tx_count || 0,
      }));
      const totalAda = sumAda(current);
      const prevAda = previous.length === half ? sumAda(previous) : null;
      const totalTx = current.reduce((a, e) => a + (e.tx_count || 0), 0);
      const blockCount = current.reduce((a, e) => a + (e.block_count || 0), 0);
      let peak = { ada: 0, epoch: current[0]?.epoch };
      current.forEach((e) => {
        const ada = Number(e.output || 0) / 1e6;
        if (ada > peak.ada) peak = { ada, epoch: e.epoch };
      });
      return {
        bars,
        stats: {
          totalTx,
          totalAda,
          avgPerBlock: blockCount ? totalAda / blockCount : 0,
          peakAda: peak.ada,
          peakLabelText: peak.ada > 0 ? `epoch ${peak.epoch}` : "—",
          unitSub: `${current.length} epochs`,
          trendPct: prevAda && prevAda > 0 ? ((totalAda - prevAda) / prevAda) * 100 : null,
        },
      };
    }

    // ---- blocks mode ----
    const valid = blocks.filter((b) => b && b.time != null).sort((a, b) => a.time - b.time);
    if (!valid.length) return { bars: [], stats: null };
    const end = valid[valid.length - 1].time;
    const start = end - cfg.minutes * 60;
    const prevStart = start - cfg.minutes * 60;
    const inRange = valid.filter((b) => b.time >= start && b.time <= end);
    const prevRange = valid.filter((b) => b.time >= prevStart && b.time < start);
    const bucketSec = cfg.bucketMin * 60;
    const nBuckets = Math.ceil((cfg.minutes * 60) / bucketSec);
    const buckets = Array.from({ length: nBuckets }, (_, i) => {
      const t = start + i * bucketSec;
      return { t, label: hhmm(t), tip: `${hhmm(t)} UTC`, ada: 0, txs: 0 };
    });
    let totalAda = 0, totalTx = 0;
    inRange.forEach((b) => {
      const ada = Number(b.output || 0) / 1e6;
      const idx = Math.min(nBuckets - 1, Math.floor((b.time - start) / bucketSec));
      buckets[idx].ada += ada;
      buckets[idx].txs += b.tx_count || 0;
      totalAda += ada;
      totalTx += b.tx_count || 0;
    });
    // Only trust the trend if we actually fetched far enough to cover the
    // full previous window (otherwise it would be an undercount).
    const coveredPrev = valid[0].time <= prevStart && prevRange.length > 0;
    const prevAda = coveredPrev ? prevRange.reduce((a, b) => a + Number(b.output || 0) / 1e6, 0) : null;
    let peak = { ada: 0, t: end };
    buckets.forEach((bk) => { if (bk.ada > peak.ada) peak = { ada: bk.ada, t: bk.t }; });
    const blockCount = inRange.length;
    return {
      bars: buckets,
      stats: {
        totalTx,
        totalAda,
        avgPerBlock: blockCount ? totalAda / blockCount : 0,
        peakAda: peak.ada,
        peakLabelText: peak.ada > 0 ? peakLabel(peak.t) : "—",
        unitSub: `${blockCount} blocks`,
        trendPct: prevAda && prevAda > 0 ? ((totalAda - prevAda) / prevAda) * 100 : null,
      },
    };
  }, [blocks, epochs, cfg]);

  // show ~7 x-axis ticks
  const tickInterval = bars.length > 7 ? Math.ceil(bars.length / 7) : 0;

  return (
    <div data-testid="transaction-volume" className="relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      {/* header */}
      <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 shrink-0 rounded-md border border-primary/25 bg-primary/[0.06] flex items-center justify-center" style={{ filter: "drop-shadow(0 0 4px rgba(157,92,255,0.4))" }}>
            <ChartBar size={12} className="text-primary" weight="bold" />
          </div>
          <span className="text-[11px] uppercase tracking-[0.25em] text-[#22d3ee] mono">transaction volume</span>
          {loading ? <CircleNotch size={11} className="animate-spin text-primary/70" /> : null}
        </div>
        <div className="inline-flex border border-primary/15 rounded-lg overflow-hidden" data-testid="tv-range-group">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`tv-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={cn(
                "px-3 py-1 text-[10px] uppercase tracking-[0.15em] mono transition-colors",
                range === r.key ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/20",
              )}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      {/* stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-primary/10 border-b border-primary/15">
        <StatTile testid="tv-stat-txs" icon={ArrowsLeftRight} label="Transactions" value={stats ? formatBigCount(stats.totalTx) : "—"} sub={stats?.unitSub ?? ""} />
        <StatTile testid="tv-stat-volume" icon={Database} label="Total volume (ADA)" value={stats ? `${formatBigCount(stats.totalAda)} ₳` : "—"} sub={`last ${range}`} badge={stats ? <TrendBadge pct={stats.trendPct} /> : null} spark={stats ? <Sparkline data={bars} up={(stats.trendPct ?? 0) >= 0} /> : null} />
        <StatTile testid="tv-stat-avg" icon={Cube} label={cfg.mode === "epochs" ? "Avg per block" : "Average per block"} value={stats ? `${formatBigCount(stats.avgPerBlock)} ₳` : "—"} sub="ADA output" />
        <StatTile testid="tv-stat-peak" icon={Lightning} label="Peak volume" value={stats ? `${formatBigCount(stats.peakAda)} ₳` : "—"} sub={stats ? stats.peakLabelText : "—"} />
      </div>

      {/* chart */}
      <div className="px-4 sm:px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">volume (ada){cfg.mode === "epochs" ? " · per epoch" : ""}</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground mono">
            <span className="inline-block h-2 w-2 rounded-full bg-primary" /> Transaction volume (ADA)
          </span>
        </div>
        {error && !stats ? (
          <div className="h-[260px] flex items-center justify-center text-[12px] text-muted-foreground mono">{error.message || "Couldn't load volume."}</div>
        ) : !stats ? (
          <div className="h-[260px] flex items-center justify-center text-[12px] text-muted-foreground mono">
            <CircleNotch size={16} className="animate-spin text-primary/70 mr-2" /> loading chain history…
          </div>
        ) : (
          <div style={{ width: "100%", height: 260 }} className="grid-bg rounded-lg" data-testid="tv-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars} margin={{ top: 8, right: 8, bottom: 6, left: 0 }} barCategoryGap={cfg.mode === "epochs" ? "22%" : 1}>
                <defs>
                  <linearGradient id="tvBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(264 100% 72%)" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="hsl(264 100% 60%)" stopOpacity={0.45} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="hsl(var(--primary))" strokeOpacity={0.1} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.15 }} interval={tickInterval} minTickGap={16} />
                <YAxis tickFormatter={yAxisFmt} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} width={48} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--primary))", fillOpacity: 0.08 }} />
                <Bar dataKey="ada" fill="url(#tvBar)" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                  {bars.map((b, i) => (
                    <Cell key={i} fill={stats && b.ada === stats.peakAda && b.ada > 0 ? "hsl(190 95% 55%)" : "url(#tvBar)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
